import { useState } from 'react';
import { useAuth } from '../../auth/AuthContext.js';
import { ClassEditor } from './ClassEditor.js';
import { WeaponEditor } from './WeaponEditor.js';
import { ItemEditor } from './ItemEditor.js';
import { CharacterEditor } from './CharacterEditor.js';
import { EnemyEditor } from './EnemyEditor.js';
import { CombatSimulator } from './CombatSimulator.js';
import { GameImporter } from './GameImporter.js';
import { StoryEditor } from './StoryEditor.js';
import { ChatbotConfig } from './ChatbotConfig.js';

export type AdminSubTab =
  | 'classes' | 'weapons' | 'items' | 'characters' | 'enemies' | 'combat'
  | 'import' | 'story' | 'chatbot';

export function AdminShell(): JSX.Element {
  const { user, loading } = useAuth();
  const [subTab, setSubTab] = useState<AdminSubTab>('classes');

  const canEdit = user !== null && (user.role === 'admin' || user.role === 'dono');

  /**
   * Sem sessão de admin, o painel diz o que falta em vez de sumir.
   *
   * Os editores de classes, personagens, armas e inimigos leem
   * `/api/admin/content/*`, que responde 401 sem sessão e 403 sem papel — e uma
   * lista vazia era tudo o que se via. Agora o motivo está escrito na tela.
   */
  if (!loading && !canEdit) {
    return (
      <div className="admin-shell-wrapper">
        <div className="admin-locked">
          <span className="admin-locked-icon">🔒</span>
          <h2>Editor de Banco de Dados</h2>
          {user === null ? (
            <p>
              Entre com uma conta de administrador para abrir os editores de classes,
              personagens, armas e inimigos.
            </p>
          ) : (
            <p>
              A conta <strong>{user.username}</strong> tem o papel{' '}
              <strong>{user.role}</strong>, que não edita o conteúdo do jogo. Peça a
              promoção para <strong>admin</strong> a quem administra o servidor.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell-wrapper">
      <header className="admin-subnav-bar">
        <div className="admin-brand-line">
          <div className="admin-brand-info">
            <span className="admin-brand-icon">⚙️</span>
            <div>
              <h1 className="admin-page-title">Editor de Banco de Dados</h1>
              <p className="admin-page-desc">
                Criação e balanceamento de conteúdo no estilo RPG Maker MZ com sincronização direta
                no MariaDB.
              </p>
            </div>
          </div>
          {user && (
            <div className="admin-user-tag">
              <span>Operando como:</span>
              <strong>{user.username}</strong>
              <span className={`admin-role-badge ${user.role}`}>{user.role.toUpperCase()}</span>
            </div>
          )}
        </div>

        <nav className="admin-tabs-row" aria-label="Navegação do Banco de Dados">
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'classes' ? 'active' : ''}`}
            onClick={() => setSubTab('classes')}
          >
            🛡️ Classes
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'weapons' ? 'active' : ''}`}
            onClick={() => setSubTab('weapons')}
          >
            ⚔️ Armas
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'items' ? 'active' : ''}`}
            onClick={() => setSubTab('items')}
          >
            🧪 Itens Consumíveis
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'characters' ? 'active' : ''}`}
            onClick={() => setSubTab('characters')}
          >
            👥 Personagens
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'enemies' ? 'active' : ''}`}
            onClick={() => setSubTab('enemies')}
          >
            👹 Inimigos
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'combat' ? 'active' : ''}`}
            onClick={() => setSubTab('combat')}
          >
            🎯 Simulador de Combate
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'story' ? 'active' : ''}`}
            onClick={() => setSubTab('story')}
          >
            📖 História
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'chatbot' ? 'active' : ''}`}
            onClick={() => setSubTab('chatbot')}
          >
            💬 Assistente
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${subTab === 'import' ? 'active' : ''}`}
            onClick={() => setSubTab('import')}
          >
            📦 Importar Jogo
          </button>
        </nav>
      </header>

      <div className="admin-tab-content-area">
        {subTab === 'classes' && <ClassEditor />}
        {subTab === 'weapons' && <WeaponEditor />}
        {subTab === 'items' && <ItemEditor />}
        {subTab === 'characters' && <CharacterEditor />}
        {subTab === 'enemies' && <EnemyEditor />}
        {subTab === 'combat' && <CombatSimulator />}
        {subTab === 'story' && <StoryEditor />}
        {subTab === 'chatbot' && <ChatbotConfig />}
        {subTab === 'import' && <GameImporter />}
      </div>
    </div>
  );
}

